import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography, Grid, CircularProgress, Box } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import * as HrActions from '../../store/actions/hr.action';
import { getHrState } from '../../store/selector/hr.selector';
import { columns } from './dashboard-column';
import Services from '../../shared/services';
import DashboardApplicationStatus from '../../shared/components/dashboard/dashboard-application-status';
import DashboardLayout from '../../shared/components/ui/dashboard-layout';
import DynamicDataGrid from '../../shared/components/ui/datagrid/datagrid';
import DynamicTabPanel from '../../shared/components/ui/dynamic-tabs/dynamic-tab-panel';
import FilterModal from '../../shared/components/ui/modal/filter-modal';
import { DASHBOARD_TABS_CONSTANT } from '../../shared/constants/dashboard-tabs.constant';

const styles = {
  button: {
    padding: '0.5rem 3rem',
    borderRadius: '2.5rem',
    width: { sm: 'auto', xs: '100%' },
  },
};

const Dashboard = () => {
  /* eslint-disable camelcase */
  const dispatch = useDispatch();
  const { reports_query } = useSelector(getHrState);

  const {
    is_loading_dashboard_list,
    dashboard_list: {
      pages,
      total,
      current_page,
      count,
      ApplicationsReport: { applications },
    },
  } = useSelector(getHrState);

  const [modalOptions, setModalOptions] = useState<{
    isModalOpen: boolean;
    onClickModal: (e: string) => void;
  }>({
    isModalOpen: false,
    onClickModal: (e: string) => onModalAction(e),
  });

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  useEffect(() => {
    if (reports_query) {
      dispatch(
        HrActions.DashboardApplicationData({
          ...reports_query,
          department: '',
          month: '',
          province: '',
          reason: '',
          signatory: '',
          status: '',
          year: '',
        }),
      );
    }
  }, []);

  const openModal = () => {
    setModalOptions((p) => ({
      ...p,
      isModalOpen: true,
    }));
  };

  const onModalAction = (e: string) => {
    if (e === 'confirm') setModalOptions((p) => ({ ...p, isModalOpen: false }));

    if (e === 'cancel') setModalOptions((p) => ({ ...p, isModalOpen: false }));
  };

  const onPaginationRequest = (page: number) => {
    const q = { ...reports_query, page: page + 1 };

    if (q) dispatch(HrActions.DashboardApplicationData(q));
  };

  const downloadDashboardAppData = async () => {
    setIsDownloading(true);
    const { data, success, errors } = await Services.api.hr.applicationDashboardExport({
      ...reports_query,
      is_export: 'application',
    });

    if (success) {
      window.open(Services.api.hr.downloadApplicationDashboardData(data), '_blank');
      Services.toast('success', 'File successfully exported.');
      setIsDownloading(false);
    } else {
      const [{ message }] = errors;
      Services.toast('error', message);
      setIsDownloading(false);
    }
  };

  return (
    <DashboardLayout>
      <Grid container>
        <Grid
          item
          xs={12}
          sx={{
            width: '100%',
            display: 'flex',
            mb: '2rem',
          }}
        >
          <Typography sx={{ fontSize: '1.75rem' }}>Dashboard</Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: { sm: 'row', xs: 'column' },
            justifyContent: { sm: 'flex-end', xs: 'center' },
            mb: '2rem',
          }}
        >
          <Button
            startIcon={<FilterListIcon />}
            sx={{ ...styles.button, mr: { sm: '1rem', xs: 0 } }}
            variant='contained'
            onClick={openModal}
            disabled={is_loading_dashboard_list ? true : false}
            disableElevation
          >
            Filter Reports
          </Button>

          <Button
            startIcon={
              isDownloading ? (
                <CircularProgress color='primary' size={15} thickness={5} />
              ) : (
                <FileDownloadIcon />
              )
            }
            sx={{ ...styles.button, mt: { sm: 0, xs: '0.2rem' } }}
            variant='outlined'
            onClick={() => {
              downloadDashboardAppData();
            }}
            disabled={is_loading_dashboard_list ? true : false}
            disableElevation
          >
            {isDownloading ? 'Downloading' : 'Download'}
          </Button>

          <FilterModal
            isModalOpen={modalOptions.isModalOpen}
            onClickModal={modalOptions.onClickModal}
          />
        </Grid>

        <DashboardApplicationStatus />

        <Grid item xs={12} sx={{ mt: '2rem', background: '#fff', height: '70vh', width: '100%' }}>
          {is_loading_dashboard_list ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress color='primary' size={40} thickness={5} />
              <Typography sx={{ mt: '2rem' }}>Loading Table</Typography>
            </Box>
          ) : (
            <DynamicDataGrid
              onPagination={(page: number) => onPaginationRequest(page)}
              columns={columns}
              rows={applications}
              config={{
                count: count,
                currentPage: current_page,
                pages: pages,
                total: total,
              }}
            />
          )}
        </Grid>

        <Grid item xs={12} sx={{ mt: '4rem' }}>
          <Typography sx={{ fontSize: '1.75rem', mb: '2rem' }}>Breakdown Report</Typography>

          <DynamicTabPanel tabs={DASHBOARD_TABS_CONSTANT} />
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default Dashboard;
